import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AlertTriangle,
  CalendarDays,
  ChefHat,
  ClipboardList,
  Download,
  PackageCheck,
  RefreshCcw,
  Truck,
} from "lucide-react";

import { jsPDF } from "jspdf";

import autoTable from "jspdf-autotable";

import { GlobalContext } from "../../../Context/GlobalContext";

const ALLOWED_USER_IDS = ["A1437", "A1443"];

const getToday = () => {
  const now = new Date();

  const offset = now.getTimezoneOffset() * 60 * 1000;

  return new Date(now.getTime() - offset).toISOString().split("T")[0];
};

const formatReportDate = (value) => {
  if (!value) return "-";

  const [year, month, day] = String(value).split("-");

  return year && month && day ? `${day}-${month}-${year}` : value;
};

const formatPrintedDateTime = (date = new Date()) => {
  const pad = (value) => String(value).padStart(2, "0");

  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

const formatDispatchTime = (order) => {
  const eventTime = order?.customization?.event?.startTime;

  if (eventTime) return eventTime;

  const scheduled = order?.scheduled_date;

  if (!scheduled) return "-";

  const match = String(scheduled).match(/(\d{1,2}:\d{2})(?:\s*(AM|PM))?/i);

  if (!match) return scheduled;

  return `${match[1]}${match[2] ? ` ${match[2].toUpperCase()}` : ""}`;
};

const getLocation = (order) =>
  order?.customization?.event?.location || order?.address_line || "-";

const getLineDescription = (order) => {
  const title =
    order?.packageName ||
    order?.actualName ||
    order?.product_id ||
    "Order item";

  const groups = order?.customization?.selectedGroups || [];

  const selectedItems = groups.flatMap((group) =>
    (group?.items || []).map((item) => {
      const qty = item?.quantityPerPerson || item?.fixedQuantity;

      return qty ? `${item.itemName} × ${qty}` : item.itemName;
    }),
  );

  return selectedItems.length ? `${title}\n${selectedItems.join("\n")}` : title;
};

const groupOrders = (orders = []) => {
  const map = new Map();

  orders.forEach((order) => {
    const key = String(order?.orderid || order?.id || "");

    if (!map.has(key)) {
      map.set(key, {
        orderId: key,

        status: order?.status || "-",

        customerMobile: order?.customer_mobile || "-",

        customerName: order?.customer_name || "",

        location: getLocation(order),

        dispatchTime: formatDispatchTime(order),

        people: Number(order?.people || order?.totalPeople || 0),

        packageQuantity: 0,

        lines: [],
      });
    }

    const current = map.get(key);

    current.packageQuantity += Number(
      order?.packageQuantity || order?.quantity || 1,
    );

    current.people = Math.max(
      current.people,

      Number(order?.people || order?.totalPeople || 0),
    );

    current.lines.push(getLineDescription(order));
  });

  return Array.from(map.values()).sort((a, b) =>
    String(a.dispatchTime).localeCompare(String(b.dispatchTime), undefined, {
      numeric: true,
    }),
  );
};

const KitchenReport = () => {
  const { api, userID } = useContext(GlobalContext);

  const isAllowedUser = ALLOWED_USER_IDS.includes(
    String(userID || "")
      .trim()

      .toUpperCase(),
  );

  const [reportDate, setReportDate] = useState(getToday);

  const [report, setReport] = useState(null);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const fetchKitchenReport = useCallback(async () => {
    if (!isAllowedUser || !reportDate) return;

    try {
      setLoading(true);

      setError("");

      const res = await api.post(
        "/Order/kitchenReportBhandarewala",

        { report_date: reportDate },

        {
          headers: {
            accept: "*/*",

            "Content-Type": "application/json-patch+json",
          },
        },
      );

      if (res?.data?.responseCode !== "00") {
        throw new Error(
          res?.data?.responseMessage || "Unable to fetch kitchen report",
        );
      }

      setReport(res?.data?.response || null);
    } catch (err) {
      console.error("Kitchen Report API Error:", err);

      setReport(null);

      setError(
        err?.response?.data?.responseMessage ||
          err?.message ||
          "Unable to fetch kitchen report",
      );
    } finally {
      setLoading(false);
    }
  }, [api, reportDate, userID, isAllowedUser]);

  useEffect(() => {
    fetchKitchenReport();
  }, [fetchKitchenReport]);

  const counts = report?.counts || {};

  const payload = report?.response || {};

  const orders = payload?.orders || [];

  const attentionRequired = payload?.attentionRequired || [];

  const preparationSummary = payload?.preparationSummary || [];

  const breads = payload?.breads || [];

  const disposables = payload?.disposables || [];

  const consolidatedItems = payload?.consolidatedItems || [];

  const orderCategoryCounts = payload?.orderCategoryCounts || [];

  const orderWisePreparation = payload?.orderWisePreparation || [];

  const dataCoverage = payload?.dataCoverage || {};

  const totalBreads = payload?.totalBreads ?? 0;

  const totalBreadsUnit = payload?.totalBreadsUnit || "";

  const groupedOrders = useMemo(() => groupOrders(orders), [orders]);

  const kitchenItems = useMemo(
    () => preparationSummary.filter((item) => item?.department === "KITCHEN"),

    [preparationSummary],
  );

  const packingItems = useMemo(
    () => preparationSummary.filter((item) => item?.department === "PACKING"),

    [preparationSummary],
  );

  const addPdfHeader = (doc, title) => {
    const printedAt = new Date();

    const reportDateText = formatReportDate(report?.reportDate || reportDate);

    const printedDateText = formatPrintedDateTime(printedAt);

    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFont("helvetica", "bold");

    doc.setFontSize(14);

    doc.text(`BHANDARE WALAS - ${title}`, pageWidth / 2, 10, {
      align: "center",
    });

    autoTable(doc, {
      startY: 14,

      head: [
        [
          "REPORT DATE",
          "PRINTED DATE",
          "PRINTED BY",
          "TOTAL ORDERS",
          "ORDER LINES",
          "PREP ITEMS",
        ],
      ],

      body: [
        [
          reportDateText,
          printedDateText,
          String(userID || ALLOWED_USER_IDS[0]),
          counts?.orderCount ?? groupedOrders.length,
          counts?.orderLineCount ?? orders.length,
          counts?.readyPreparationRows ?? preparationSummary.length,
        ],
      ],

      styles: {
        fontSize: 8,

        cellPadding: 1.7,

        halign: "center",

        valign: "middle",

        lineWidth: 0.15,

        lineColor: [100, 100, 100],
      },

      headStyles: {
        fillColor: [235, 246, 238],

        textColor: [25, 60, 35],

        fontStyle: "bold",
      },

      bodyStyles: {
        textColor: [30, 30, 30],

        fontStyle: "bold",
      },

      margin: { left: 8, right: 8 },
    });

    return (doc.lastAutoTable?.finalY || 22) + 3;
  };

  const downloadOrderSummaryPdf = () => {
    if (!report) return;

    const doc = new jsPDF({
      orientation: "landscape",

      unit: "mm",

      format: "a4",
    });

    const startY = addPdfHeader(doc, "DISPATCH & ORDER PLAN");

    autoTable(doc, {
      startY,

      head: [
        [
          "STATUS",

          "SEQ.",

          "ORDER NO.",

          "CUSTOMER",

          "ITEMS",

          "PEOPLE",

          "QTY.",

          "LOCATION",

          "DISPATCH",
        ],
      ],

      body: groupedOrders.map((order, index) => [
        String(order.status || "-").toUpperCase(),

        index + 1,

        order.orderId || "-",

        order.customerMobile,

        order.lines.join("\n\n"),

        order.people || "-",

        order.packageQuantity || "-",

        order.location,

        order.dispatchTime,
      ]),

      styles: {
        fontSize: 6.8,

        cellPadding: 1.4,

        valign: "top",

        lineWidth: 0.12,

        lineColor: [95, 95, 95],
      },

      headStyles: {
        fillColor: [235, 246, 238],

        textColor: [25, 60, 35],

        fontStyle: "bold",
      },

      columnStyles: {
        0: { cellWidth: 16 },

        1: { cellWidth: 11, halign: "center" },

        2: { cellWidth: 20, halign: "center" },

        3: { cellWidth: 25 },

        4: { cellWidth: 89 },

        5: { cellWidth: 15, halign: "center" },

        6: { cellWidth: 14, halign: "center" },

        7: { cellWidth: 54 },

        8: { cellWidth: 21 },
      },

      margin: { left: 8, right: 8 },
    });

    doc.addPage();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("ORDER CATEGORY COUNTS", 8, 10);

    autoTable(doc, {
      startY: 12,
      head: [
        [
          "CATEGORY",
          "LINE COUNT",
          "ORDER COUNT",
          "ORDERED QUANTITY",
          "QUANTITY UNIT",
        ],
      ],
      body: orderCategoryCounts.map((item) => [
        item?.category || "-",
        item?.lineCount ?? "-",
        item?.orderCount ?? "-",
        item?.orderedQuantity ?? "-",
        item?.quantityUnit || "-",
      ]),
      styles: {
        fontSize: 7.2,
        cellPadding: 1.5,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [235, 246, 238],
        textColor: [25, 60, 35],
        fontStyle: "bold",
      },
      margin: { left: 8, right: 8 },
    });

    doc.addPage();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("ORDER WISE PREPARATION", 8, 10);

    autoTable(doc, {
      startY: 12,
      head: [
        [
          "ORDER ID",
          "DAILY ORDER ID",
          "ORDERED ITEM",
          "PACKAGE",
          "PREPARATION ITEM",
          "QTY.",
          "UNIT",
          "PEOPLE",
          "TOTAL PEOPLE",
          "PACKAGE QTY.",
          "DEPARTMENT",
          "SOURCE TYPE",
          "RESOLUTION",
          "ORDER ITEM ID",
          "PACKAGE ID",
          "CATALOGUE ID",
          "COMPONENT ID",
        ],
      ],
      body: orderWisePreparation.map((item) => [
        item?.orderId || "-",
        item?.dailyOrderId ?? "-",
        item?.orderedItem || "-",
        item?.packageName || "-",
        item?.itemName || "-",
        item?.quantity ?? "-",
        item?.unit || "-",
        item?.people ?? "-",
        item?.totalPeople ?? "-",
        item?.packageQuantity ?? "-",
        item?.department || "-",
        item?.sourceType || "-",
        item?.resolutionStatus || "-",
        item?.orderItemId ?? "-",
        item?.packageId ?? "-",
        item?.catalogueItemId ?? "-",
        item?.reportComponentId ?? "-",
      ]),
      styles: {
        fontSize: 5.7,
        cellPadding: 1,
        lineWidth: 0.08,
        valign: "top",
      },
      headStyles: {
        fillColor: [235, 246, 238],
        textColor: [25, 60, 35],
        fontStyle: "bold",
      },
      margin: { left: 5, right: 5 },
    });

    doc.save(`Order-Summary-${report?.reportDate || reportDate}.pdf`);
  };

  const downloadPreparationSummaryPdf = () => {
    if (!report) return;

    const doc = new jsPDF({
      orientation: "landscape",

      unit: "mm",

      format: "a4",
    });

    let nextY = addPdfHeader(doc, "PREPARATION SUMMARY");

    const availableBottom = doc.internal.pageSize.getHeight() - 10;

    autoTable(doc, {
      startY: nextY,

      head: [["DEPARTMENT", "ITEM", "QUANTITY", "UNIT", "ORDERS"]],

      body: preparationSummary.map((item) => [
        item?.department || "-",

        item?.itemName || "-",

        item?.quantity ?? "-",

        item?.unit || "-",

        item?.orderCount ?? "-",
      ]),

      styles: {
        fontSize: 7.4,

        cellPadding: 1.5,

        lineWidth: 0.1,

        valign: "middle",
      },

      headStyles: {
        fillColor: [235, 246, 238],

        textColor: [25, 60, 35],

        fontStyle: "bold",
      },

      columnStyles: {
        0: { cellWidth: 38 },

        1: { cellWidth: 130 },

        2: { cellWidth: 32, halign: "right" },

        3: { cellWidth: 30 },

        4: { cellWidth: 28, halign: "right" },
      },

      margin: { left: 8, right: 8 },
    });

    // if (attentionRequired.length) {
    //   nextY = (doc.lastAutoTable?.finalY || 20) + 5;

    //   if (nextY > availableBottom - 28) {
    //     doc.addPage();

    //     nextY = 10;
    //   }

    //   doc.setFont("helvetica", "bold");

    //   doc.setFontSize(10);

    //   doc.text("ATTENTION REQUIRED", 8, nextY);

    //   autoTable(doc, {
    //     startY: nextY + 2,

    //     head: [["ORDER NO.", "PACKAGE", "CHOICE GROUP", "MESSAGE"]],

    //     body: attentionRequired.map((item) => [
    //       item?.orderId || "-",

    //       item?.packageName || "-",

    //       item?.choiceGroup || "-",

    //       item?.message || "-",
    //     ]),

    //     styles: { fontSize: 7.4, cellPadding: 1.5, lineWidth: 0.1 },

    //     headStyles: {
    //       fillColor: [255, 247, 214],

    //       textColor: [120, 80, 0],

    //       fontStyle: "bold",
    //     },

    //     margin: { left: 8, right: 8 },
    //   });
    // }


    doc.addPage();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("BREADS", 8, 10);

    autoTable(doc, {
      startY: 12,
      head: [["ITEM", "QUANTITY", "UNIT"]],
      body: breads.map((item) => [
        item?.itemName || "-",
        item?.quantity ?? "-",
        item?.unit || "-",
      ]),
      styles: {
        fontSize: 7.4,
        cellPadding: 1.5,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [235, 246, 238],
        textColor: [25, 60, 35],
        fontStyle: "bold",
      },
      margin: { left: 8, right: 8 },
    });

    doc.addPage();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("DISPOSABLES", 8, 10);

    autoTable(doc, {
      startY: 12,
      head: [["ITEM", "QUANTITY", "UNIT"]],
      body: disposables.map((item) => [
        item?.itemName || "-",
        item?.quantity ?? "-",
        item?.unit || "-",
      ]),
      styles: {
        fontSize: 7.4,
        cellPadding: 1.5,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [235, 246, 238],
        textColor: [25, 60, 35],
        fontStyle: "bold",
      },
      margin: { left: 8, right: 8 },
    });

    doc.addPage();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("CONSOLIDATED ITEMS", 8, 10);

    autoTable(doc, {
      startY: 12,
      head: [
        [
          "ITEM",
          "CATEGORY",
          "TOTAL",
          "UNIT",
          "INDIVIDUAL",
          "PACKAGES",
          "COMBOS",
          "THALIS",
          "UNCLASSIFIED",
          "ORDERS",
          "COMPONENT ID",
        ],
      ],
      body: consolidatedItems.map((item) => [
        item?.itemName || "-",
        item?.category || "-",
        item?.total ?? "-",
        item?.unit || "-",
        item?.individual ?? "-",
        item?.packages ?? "-",
        item?.combos ?? "-",
        item?.thalis ?? "-",
        item?.unclassifiedPackages ?? "-",
        item?.orderCount ?? "-",
        item?.reportComponentId ?? "-",
      ]),
      styles: {
        fontSize: 6.4,
        cellPadding: 1.2,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [235, 246, 238],
        textColor: [25, 60, 35],
        fontStyle: "bold",
      },
      margin: { left: 8, right: 8 },
    });

    doc.save(`Preparation-Summary-${report?.reportDate || reportDate}.pdf`);
  };

  const downloadMergedSummaryPdf = () => {
    if (!report) return;

    const doc = new jsPDF({
      orientation: "landscape",

      unit: "mm",

      format: "a4",
    });

    let nextY = addPdfHeader(doc, "KITCHEN REPORT - COMPLETE SUMMARY");

    const availableBottom = doc.internal.pageSize.getHeight() - 10;

    doc.setFont("helvetica", "bold");

    doc.setFontSize(10);

    doc.text("DISPATCH & ORDER PLAN", 8, nextY);

    autoTable(doc, {
      startY: nextY + 2,

      head: [
        [
          "STATUS",

          "SEQ.",

          "ORDER NO.",

          "CUSTOMER",

          "ITEMS",

          "PEOPLE",

          "QTY.",

          "LOCATION",

          "DISPATCH",
        ],
      ],

      body: groupedOrders.map((order, index) => [
        String(order.status || "-").toUpperCase(),

        index + 1,

        order.orderId || "-",

        order.customerMobile,

        order.lines.join("\n\n"),

        order.people || "-",

        order.packageQuantity || "-",

        order.location,

        order.dispatchTime,
      ]),

      styles: {
        fontSize: 6.8,

        cellPadding: 1.4,

        valign: "top",

        lineWidth: 0.12,

        lineColor: [95, 95, 95],
      },

      headStyles: {
        fillColor: [235, 246, 238],

        textColor: [25, 60, 35],

        fontStyle: "bold",
      },

      columnStyles: {
        0: { cellWidth: 16 },

        1: { cellWidth: 11, halign: "center" },

        2: { cellWidth: 20, halign: "center" },

        3: { cellWidth: 25 },

        4: { cellWidth: 89 },

        5: { cellWidth: 15, halign: "center" },

        6: { cellWidth: 14, halign: "center" },

        7: { cellWidth: 54 },

        8: { cellWidth: 21 },
      },

      margin: { left: 8, right: 8 },
    });

    nextY = (doc.lastAutoTable?.finalY || 20) + 5;

    if (nextY > availableBottom - 28) {
      doc.addPage();

      nextY = 10;
    }

    doc.setFont("helvetica", "bold");

    doc.setFontSize(10);

    doc.text("PREPARATION SUMMARY", 8, nextY);

    autoTable(doc, {
      startY: nextY + 2,

      head: [["DEPARTMENT", "ITEM", "QUANTITY", "UNIT", "ORDERS"]],

      body: preparationSummary.map((item) => [
        item?.department || "-",

        item?.itemName || "-",

        item?.quantity ?? "-",

        item?.unit || "-",

        item?.orderCount ?? "-",
      ]),

      styles: {
        fontSize: 7.4,

        cellPadding: 1.5,

        lineWidth: 0.1,

        valign: "middle",
      },

      headStyles: {
        fillColor: [235, 246, 238],

        textColor: [25, 60, 35],

        fontStyle: "bold",
      },

      columnStyles: {
        0: { cellWidth: 38 },

        1: { cellWidth: 130 },

        2: { cellWidth: 32, halign: "right" },

        3: { cellWidth: 30 },

        4: { cellWidth: 28, halign: "right" },
      },

      margin: { left: 8, right: 8 },
    });

    // if (attentionRequired.length) {
    //   nextY = (doc.lastAutoTable?.finalY || 20) + 5;

    //   if (nextY > availableBottom - 28) {
    //     doc.addPage();

    //     nextY = 10;
    //   }

    //   doc.setFont("helvetica", "bold");

    //   doc.setFontSize(10);

    //   doc.text("ATTENTION REQUIRED", 8, nextY);

    //   autoTable(doc, {
    //     startY: nextY + 2,

    //     head: [["ORDER NO.", "PACKAGE", "CHOICE GROUP", "MESSAGE"]],

    //     body: attentionRequired.map((item) => [
    //       item?.orderId || "-",

    //       item?.packageName || "-",

    //       item?.choiceGroup || "-",

    //       item?.message || "-",
    //     ]),

    //     styles: { fontSize: 7.4, cellPadding: 1.5, lineWidth: 0.1 },

    //     headStyles: {
    //       fillColor: [255, 247, 214],

    //       textColor: [120, 80, 0],

    //       fontStyle: "bold",
    //     },

    //     margin: { left: 8, right: 8 },
    //   });
    // }

    doc.addPage();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("REPORT METADATA", 8, 10);

    autoTable(doc, {
      startY: 12,
      head: [["FIELD", "VALUE"]],
      body: [
        ["Merchant ID", report?.merchantId ?? "-"],
        ["Date To", formatReportDate(report?.dateTo) || "-"],
        ["Payment Filter", report?.paymentFilter || "-"],
        ["Refund Policy", report?.refundPolicy || "-"],
        ["Response Code", report?.responseCode || "-"],
        ["Response Message", report?.responseMessage || "-"],
        ["Total Breads", `${totalBreads ?? 0} ${totalBreadsUnit || ""}`.trim()],
      ],
      styles: {
        fontSize: 7.2,
        cellPadding: 1.5,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [235, 246, 238],
        textColor: [25, 60, 35],
        fontStyle: "bold",
      },
      margin: { left: 8, right: 8 },
    });

    // ---------------- BREADS ----------------
    if (breads.length) {
      doc.addPage();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("BREADS", 8, 10);

      autoTable(doc, {
        startY: 12,
        head: [["ITEM", "QUANTITY", "UNIT"]],
        body: breads.map((item) => [
          item?.itemName || "-",
          item?.quantity ?? "-",
          item?.unit || "-",
        ]),
        styles: {
          fontSize: 7.4,
          cellPadding: 1.5,
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [235, 246, 238],
          textColor: [25, 60, 35],
          fontStyle: "bold",
        },
        margin: { left: 8, right: 8 },
      });
    }

    // ---------------- DISPOSABLES ----------------
    if (disposables.length) {
      doc.addPage();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("DISPOSABLES", 8, 10);

      autoTable(doc, {
        startY: 12,
        head: [["ITEM", "QUANTITY", "UNIT"]],
        body: disposables.map((item) => [
          item?.itemName || "-",
          item?.quantity ?? "-",
          item?.unit || "-",
        ]),
        styles: {
          fontSize: 7.4,
          cellPadding: 1.5,
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [235, 246, 238],
          textColor: [25, 60, 35],
          fontStyle: "bold",
        },
        margin: { left: 8, right: 8 },
      });
    }

    // ---------------- CONSOLIDATED ITEMS ----------------
    if (consolidatedItems.length) {
      doc.addPage();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("CONSOLIDATED ITEMS", 8, 10);

      autoTable(doc, {
        startY: 12,
        head: [
          [
            "ITEM",
            "CATEGORY",
            "TOTAL",
            "UNIT",
            "INDIVIDUAL",
            "PACKAGES",
            "COMBOS",
            "THALIS",
            "UNCLASSIFIED",
            "ORDERS",
            "COMPONENT ID",
          ],
        ],
        body: consolidatedItems.map((item) => [
          item?.itemName || "-",
          item?.category || "-",
          item?.total ?? "-",
          item?.unit || "-",
          item?.individual ?? "-",
          item?.packages ?? "-",
          item?.combos ?? "-",
          item?.thalis ?? "-",
          item?.unclassifiedPackages ?? "-",
          item?.orderCount ?? "-",
          item?.reportComponentId ?? "-",
        ]),
        styles: {
          fontSize: 6.4,
          cellPadding: 1.2,
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [235, 246, 238],
          textColor: [25, 60, 35],
          fontStyle: "bold",
        },
        margin: { left: 8, right: 8 },
      });
    }

    // ---------------- ORDER CATEGORY COUNTS ----------------
    if (orderCategoryCounts.length) {
      doc.addPage();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("ORDER CATEGORY COUNTS", 8, 10);

      autoTable(doc, {
        startY: 12,
        head: [
          [
            "CATEGORY",
            "LINE COUNT",
            "ORDER COUNT",
            "ORDERED QUANTITY",
            "QUANTITY UNIT",
          ],
        ],
        body: orderCategoryCounts.map((item) => [
          item?.category || "-",
          item?.lineCount ?? "-",
          item?.orderCount ?? "-",
          item?.orderedQuantity ?? "-",
          item?.quantityUnit || "-",
        ]),
        styles: {
          fontSize: 7.2,
          cellPadding: 1.5,
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [235, 246, 238],
          textColor: [25, 60, 35],
          fontStyle: "bold",
        },
        margin: { left: 8, right: 8 },
      });
    }

    // ---------------- ORDER WISE PREPARATION ----------------
    if (orderWisePreparation.length) {
      doc.addPage();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("ORDER WISE PREPARATION", 8, 10);

      autoTable(doc, {
        startY: 12,
        head: [
          [
            "ORDER ID",
            "DAILY ORDER ID",
            "ORDERED ITEM",
            "PACKAGE",
            "PREPARATION ITEM",
            "QTY.",
            "UNIT",
            "PEOPLE",
            "TOTAL PEOPLE",
            "PACKAGE QTY.",
            "DEPARTMENT",
            "SOURCE TYPE",
            "RESOLUTION",
            "ORDER ITEM ID",
            "PACKAGE ID",
            "CATALOGUE ID",
            "COMPONENT ID",
          ],
        ],
        body: orderWisePreparation.map((item) => [
          item?.orderId || "-",
          item?.dailyOrderId ?? "-",
          item?.orderedItem || "-",
          item?.packageName || "-",
          item?.itemName || "-",
          item?.quantity ?? "-",
          item?.unit || "-",
          item?.people ?? "-",
          item?.totalPeople ?? "-",
          item?.packageQuantity ?? "-",
          item?.department || "-",
          item?.sourceType || "-",
          item?.resolutionStatus || "-",
          item?.orderItemId ?? "-",
          item?.packageId ?? "-",
          item?.catalogueItemId ?? "-",
          item?.reportComponentId ?? "-",
        ]),
        styles: {
          fontSize: 5.7,
          cellPadding: 1,
          lineWidth: 0.08,
          valign: "top",
        },
        headStyles: {
          fillColor: [235, 246, 238],
          textColor: [25, 60, 35],
          fontStyle: "bold",
        },
        margin: { left: 5, right: 5 },
      });
    }

    doc.save(`Kitchen-Report-Complete-${report?.reportDate || reportDate}.pdf`);
  };

  if (!isAllowedUser) return null;

  const statItems = [
    {
      label: "Orders",

      value: counts?.orderCount ?? groupedOrders.length,

      icon: ClipboardList,

      iconClass: "text-blue-600",
    },

    {
      label: "Order Lines",

      value: counts?.orderLineCount ?? orders.length,

      icon: PackageCheck,

      iconClass: "text-green-600",
    },

    {
      label: "Prep Items",

      value: counts?.readyPreparationRows ?? preparationSummary.length,

      icon: ChefHat,

      iconClass: "text-violet-600",
    },

    {
      label: "Attention",

      value: counts?.attentionCount ?? attentionRequired.length,

      icon: AlertTriangle,

      iconClass: "text-amber-600",
    },
  ];

  return (
    <div className="space-y-3 text-sm">
      <div className="bg-white border rounded-lg px-3 py-2.5">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-2.5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-gray-800">
              <ChefHat size={18} className="text-green-600 flex-shrink-0" />

              <h2 className="font-semibold text-[15px]">Kitchen Report</h2>

              <span className="hidden sm:inline text-xs text-gray-400">•</span>

              <span className="hidden sm:inline text-xs text-gray-500 truncate">
                Daily production, packing & dispatch
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <CalendarDays
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />

              <input
                type="date"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
                className="h-8 border rounded-md pl-8 pr-2 text-xs bg-white"
              />
            </div>

            <button
              type="button"
              onClick={downloadPreparationSummaryPdf}
              disabled={!report || loading || preparationSummary.length === 0}
              className="h-8 inline-flex items-center gap-1.5 bg-blue-600 text-white px-2.5 rounded-md text-xs font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={14} />
              Preparation Summary
            </button>

            <button
              type="button"
              onClick={downloadOrderSummaryPdf}
              disabled={!report || loading || groupedOrders.length === 0}
              className="h-8 inline-flex items-center gap-1.5 bg-blue-600 text-white px-2.5 rounded-md text-xs font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={14} />
              Order Summary
            </button>

            <button
              type="button"
              onClick={downloadMergedSummaryPdf}
              disabled={
                !report ||
                loading ||
                (groupedOrders.length === 0 && preparationSummary.length === 0)
              }
              className="h-8 inline-flex items-center gap-1.5 bg-blue-600 text-white px-2.5 rounded-md text-xs font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={14} />
              Complete Report
            </button>

            <button
              type="button"
              onClick={fetchKitchenReport}
              disabled={loading}
              className="h-8 inline-flex items-center gap-1.5 bg-green-600 text-white px-2.5 rounded-md text-xs font-medium hover:bg-green-700 disabled:opacity-60"
            >
              <RefreshCcw size={13} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-2 pt-2 border-t flex flex-wrap items-center gap-x-5 gap-y-1.5">
          <div className="text-xs text-gray-500">
            Date:{" "}
            <span className="font-semibold text-gray-700">
              {formatReportDate(report?.reportDate || reportDate)}
            </span>
          </div>

          {statItems.map(({ label, value, icon: Icon, iconClass }) => (
            <div
              key={label}
              className="inline-flex items-center gap-1.5 text-xs"
            >
              <Icon size={13} className={iconClass} />

              <span className="text-gray-500">{label}</span>

              <span className="font-semibold text-gray-800">
                {loading ? "—" : value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-xs">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="bg-white border rounded-lg min-h-[220px] flex items-center justify-center text-gray-500 text-xs">
          <RefreshCcw size={16} className="animate-spin mr-2" />
          Loading kitchen report...
        </div>
      ) : !report ? (
        <div className="bg-white border rounded-lg min-h-[220px] flex items-center justify-center text-gray-400 text-xs">
          No kitchen report found for {formatReportDate(reportDate)}.
        </div>
      ) : (
        <>
          {/* {attentionRequired.length > 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-amber-200 flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5 font-semibold text-amber-900 text-xs">
                  <AlertTriangle size={14} />
                  Attention Required ({attentionRequired.length})
                </div>
              </div>

              <div className="overflow-auto max-h-[320px]">
                <table className="w-full text-[11px] min-w-[1100px]">
                  <thead className="sticky top-0 bg-amber-100 text-amber-900 border-b border-amber-200 z-10">
                    <tr>
                      <th className="text-left px-3 py-2">Order No.</th>

                      <th className="text-left px-3 py-2">Issue Type</th>

                      <th className="text-left px-3 py-2">Package</th>

                      <th className="text-left px-3 py-2">Choice Group</th>

                      <th className="text-right px-3 py-2">Package ID</th>

                      <th className="text-right px-3 py-2">Order Item ID</th>

                      <th className="text-left px-3 py-2 min-w-[420px]">
                        Message
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {attentionRequired.map((item, index) => (
                      <tr
                        key={`${item?.orderId}-${item?.packageId}-${item?.orderItemId}-${index}`}
                        className="border-b border-amber-100 last:border-b-0"
                      >
                        <td className="px-3 py-2 font-semibold text-amber-900 whitespace-nowrap">
                          #{item?.orderId || "-"}
                        </td>

                        <td className="px-3 py-2 font-medium text-amber-800">
                          {item?.issueType || "-"}
                        </td>

                        <td className="px-3 py-2 text-gray-800">
                          {item?.packageName || "-"}
                        </td>

                        <td className="px-3 py-2 text-gray-700">
                          {item?.choiceGroup || "-"}
                        </td>

                        <td className="px-3 py-2 text-right text-gray-700">
                          {item?.packageId ?? "-"}
                        </td>

                        <td className="px-3 py-2 text-right text-gray-700">
                          {item?.orderItemId ?? "-"}
                        </td>

                        <td className="px-3 py-2 text-amber-800">
                          {item?.message || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null} */}

          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Truck size={15} className="text-gray-500 flex-shrink-0" />

                <span className="font-semibold text-gray-800 text-xs">
                  Dispatch & Order Plan
                </span>

                <span className="text-[11px] text-gray-400">
                  • {groupedOrders.length} orders
                </span>
              </div>
            </div>

            <div className="overflow-auto max-h-[46vh]">
              <table className="w-full text-[11px] min-w-[980px] leading-[1.25]">
                <thead className="sticky top-0 z-10 bg-green-50 text-gray-600 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                  <tr>
                    <th className="text-left px-2.5 py-2 w-[72px]">Status</th>

                    <th className="text-center px-2 py-2 w-[42px]">Seq.</th>

                    <th className="text-center px-2 py-2 w-[78px]">
                      Order No.
                    </th>

                    <th className="text-left px-2.5 py-2 w-[125px]">
                      Customer
                    </th>

                    <th className="text-left px-2.5 py-2 min-w-[310px]">
                      Items
                    </th>

                    <th className="text-center px-2 py-2 w-[55px]">People</th>

                    <th className="text-center px-2 py-2 w-[48px]">Qty.</th>

                    <th className="text-left px-2.5 py-2 min-w-[150px]">
                      Location
                    </th>

                    <th className="text-left px-2.5 py-2 w-[82px]">Dispatch</th>
                  </tr>
                </thead>

                <tbody>
                  {groupedOrders.map((order, index) => (
                    <tr
                      key={order.orderId}
                      className="border-t align-top hover:bg-green-50/40"
                    >
                      <td className="px-2.5 py-2">
                        <span className="inline-flex rounded bg-blue-50 text-blue-700 px-1.5 py-0.5 text-[10px] font-medium capitalize">
                          {order.status}
                        </span>
                      </td>

                      <td className="px-2 py-2 text-center font-semibold text-gray-600">
                        {index + 1}
                      </td>

                      <td className="px-2 py-2 text-center font-semibold text-gray-700 whitespace-nowrap">
                        {order.orderId}
                      </td>

                      <td className="px-2.5 py-2">
                        <div className="font-medium text-gray-800 whitespace-nowrap">
                          {order.customerMobile}
                        </div>

                        {order.customerName ? (
                          <div className="text-[10px] text-gray-500 truncate mt-0.5">
                            {order.customerName}
                          </div>
                        ) : null}
                      </td>

                      <td className="px-2.5 py-2">
                        <div className="space-y-1.5">
                          {order.lines.map((line, lineIndex) => {
                            const [title, ...items] = line.split("\n");

                            return (
                              <div key={`${order.orderId}-${lineIndex}`}>
                                <div className="font-semibold text-gray-800">
                                  {title}
                                </div>

                                {items.length ? (
                                  <div className="text-[10px] text-gray-600 mt-0.5 leading-[1.35] grid sm:grid-cols-2 gap-x-3">
                                    {items.map((item, itemIndex) => (
                                      <div
                                        key={`${lineIndex}-${itemIndex}`}
                                        className="truncate"
                                        title={item}
                                      >
                                        • {item}
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </td>

                      <td className="px-2 py-2 text-center font-semibold">
                        {order.people || "-"}
                      </td>

                      <td className="px-2 py-2 text-center">
                        {order.packageQuantity || "-"}
                      </td>

                      <td className="px-2.5 py-2 max-w-[210px] break-words">
                        {order.location}
                      </td>

                      <td className="px-2.5 py-2 font-semibold whitespace-nowrap">
                        {order.dispatchTime}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid xl:grid-cols-2 gap-3">
            <PreparationTable
              title="Kitchen Preparation"
              icon={<ChefHat size={14} className="text-green-600" />}
              rows={kitchenItems}
            />

            <PreparationTable
              title="Packing Preparation"
              icon={<PackageCheck size={14} className="text-blue-600" />}
              rows={packingItems}
            />
          </div>

          <ReportMetaCard
            merchantId={report?.merchantId}
            dateTo={report?.dateTo}
            paymentFilter={report?.paymentFilter}
            refundPolicy={report?.refundPolicy}
            responseCode={report?.responseCode}
            responseMessage={report?.responseMessage}
            totalBreads={totalBreads}
            totalBreadsUnit={totalBreadsUnit}
          />

          <div className="grid xl:grid-cols-2 gap-3">
            <SimpleItemSummaryTable title="Breads" rows={breads} />

            <SimpleItemSummaryTable title="Disposables" rows={disposables} />
          </div>

          <ConsolidatedItemsTable rows={consolidatedItems} />

          <OrderCategoryCountsTable rows={orderCategoryCounts} />

          <OrderWisePreparationTable rows={orderWisePreparation} />

          {/* <DataCoverageCard data={dataCoverage} /> */}
        </>
      )}
    </div>
  );
};

const ReportMetaCard = ({
  merchantId,

  dateTo,

  paymentFilter,

  refundPolicy,

  responseCode,

  responseMessage,

  totalBreads,

  totalBreadsUnit,
}) => {
  const rows = [
    ["Merchant ID", merchantId ?? "-"],

    ["Date To", formatReportDate(dateTo) || "-"],

    ["Payment Filter", paymentFilter || "-"],

    ["Refund Policy", refundPolicy || "-"],

    ["Response Code", responseCode || "-"],

    ["Response Message", responseMessage || "-"],

    ["Total Breads", `${totalBreads ?? 0} ${totalBreadsUnit || ""}`.trim()],
  ];

  return (
    <div className="bg-white border rounded-lg overflow-hidden">
      <div className="px-3 py-2 border-b bg-gray-50 font-semibold text-gray-800 text-xs">
        Report Metadata
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4">
        {rows.map(([label, value]) => (
          <div key={label} className="px-3 py-2 border-b sm:border-r text-xs">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">
              {label}
            </div>

            <div className="mt-0.5 font-medium text-gray-800 break-words">
              {String(value)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SimpleItemSummaryTable = ({ title, rows = [] }) => (
  <div className="bg-white border rounded-lg overflow-hidden">
    <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
      <span className="font-semibold text-gray-800 text-xs">{title}</span>

      <span className="text-[10px] text-gray-400">{rows.length} items</span>
    </div>

    <div className="overflow-auto max-h-[320px]">
      <table className="w-full text-[11px]">
        <thead className="sticky top-0 bg-white text-gray-500 border-b z-10">
          <tr>
            <th className="text-left px-3 py-2">Item</th>

            <th className="text-right px-3 py-2 w-[100px]">Quantity</th>

            <th className="text-left px-3 py-2 w-[90px]">Unit</th>
          </tr>
        </thead>

        <tbody>
          {rows.length ? (
            rows.map((item, index) => (
              <tr
                key={`${title}-${item?.itemName}-${item?.unit}-${index}`}
                className="border-b last:border-b-0 hover:bg-gray-50"
              >
                <td className="px-3 py-1.5 font-medium text-gray-800">
                  {item?.itemName || "-"}
                </td>

                <td className="px-3 py-1.5 text-right font-semibold">
                  {item?.quantity ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-gray-600">
                  {item?.unit || "-"}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3} className="px-3 py-8 text-center text-gray-400">
                No items
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const ConsolidatedItemsTable = ({ rows = [] }) => (
  <div className="bg-white border rounded-lg overflow-hidden">
    <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
      <span className="font-semibold text-gray-800 text-xs">
        Consolidated Items
      </span>

      <span className="text-[10px] text-gray-400">{rows.length} rows</span>
    </div>

    <div className="overflow-auto max-h-[420px]">
      <table className="w-full text-[11px] min-w-[1180px]">
        <thead className="sticky top-0 bg-green-50 text-gray-600 border-b z-10">
          <tr>
            <th className="text-left px-3 py-2">Item</th>

            <th className="text-left px-3 py-2">Category</th>

            <th className="text-right px-3 py-2">Total</th>

            <th className="text-left px-3 py-2">Unit</th>

            <th className="text-right px-3 py-2">Individual</th>

            <th className="text-right px-3 py-2">Packages</th>

            <th className="text-right px-3 py-2">Combos</th>

            <th className="text-right px-3 py-2">Thalis</th>

            <th className="text-right px-3 py-2">Unclassified</th>

            <th className="text-right px-3 py-2">Orders</th>

            <th className="text-right px-3 py-2">Component ID</th>
          </tr>
        </thead>

        <tbody>
          {rows.length ? (
            rows.map((item, index) => (
              <tr
                key={`${item?.reportComponentId}-${item?.itemName}-${item?.unit}-${index}`}
                className="border-b last:border-b-0 hover:bg-green-50/40"
              >
                <td className="px-3 py-1.5 font-medium text-gray-800">
                  {item?.itemName || "-"}
                </td>

                <td className="px-3 py-1.5 text-gray-600">
                  {item?.category || "-"}
                </td>

                <td className="px-3 py-1.5 text-right font-semibold">
                  {item?.total ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-gray-600">
                  {item?.unit || "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.individual ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.packages ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.combos ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.thalis ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.unclassifiedPackages ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.orderCount ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.reportComponentId ?? "-"}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={11} className="px-3 py-8 text-center text-gray-400">
                No consolidated items
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const OrderCategoryCountsTable = ({ rows = [] }) => (
  <div className="bg-white border rounded-lg overflow-hidden">
    <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
      <span className="font-semibold text-gray-800 text-xs">
        Order Category Counts
      </span>

      <span className="text-[10px] text-gray-400">
        {rows.length} categories
      </span>
    </div>

    <div className="overflow-auto">
      <table className="w-full text-[11px] min-w-[680px]">
        <thead className="bg-green-50 text-gray-600 border-b">
          <tr>
            <th className="text-left px-3 py-2">Category</th>

            <th className="text-right px-3 py-2">Line Count</th>

            <th className="text-right px-3 py-2">Order Count</th>

            <th className="text-right px-3 py-2">Ordered Quantity</th>

            <th className="text-left px-3 py-2">Quantity Unit</th>
          </tr>
        </thead>

        <tbody>
          {rows.length ? (
            rows.map((item, index) => (
              <tr
                key={`${item?.category}-${index}`}
                className="border-b last:border-b-0 hover:bg-gray-50"
              >
                <td className="px-3 py-1.5 font-medium text-gray-800">
                  {item?.category || "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.lineCount ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.orderCount ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right font-semibold">
                  {item?.orderedQuantity ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-gray-600">
                  {item?.quantityUnit || "-"}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} className="px-3 py-8 text-center text-gray-400">
                No category counts
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const OrderWisePreparationTable = ({ rows = [] }) => (
  <div className="bg-white border rounded-lg overflow-hidden">
    <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
      <span className="font-semibold text-gray-800 text-xs">
        Order Wise Preparation
      </span>

      <span className="text-[10px] text-gray-400">{rows.length} rows</span>
    </div>

    <div className="overflow-auto max-h-[520px]">
      <table className="w-full text-[11px] min-w-[1750px]">
        <thead className="sticky top-0 bg-green-50 text-gray-600 border-b z-10">
          <tr>
            <th className="text-left px-3 py-2">Order ID</th>

            <th className="text-left px-3 py-2">Daily Order ID</th>

            <th className="text-left px-3 py-2">Ordered Item</th>

            <th className="text-left px-3 py-2">Package</th>

            <th className="text-left px-3 py-2">Preparation Item</th>

            <th className="text-right px-3 py-2">Quantity</th>

            <th className="text-left px-3 py-2">Unit</th>

            <th className="text-right px-3 py-2">People</th>

            <th className="text-right px-3 py-2">Total People</th>

            <th className="text-right px-3 py-2">Package Qty</th>

            <th className="text-left px-3 py-2">Department</th>

            <th className="text-left px-3 py-2">Source Type</th>

            <th className="text-left px-3 py-2">Resolution</th>

            <th className="text-right px-3 py-2">Order Item ID</th>

            <th className="text-right px-3 py-2">Package ID</th>

            <th className="text-right px-3 py-2">Catalogue Item ID</th>

            <th className="text-right px-3 py-2">Component ID</th>
          </tr>
        </thead>

        <tbody>
          {rows.length ? (
            rows.map((item, index) => (
              <tr
                key={`${item?.orderId}-${item?.orderItemId}-${item?.reportComponentId}-${index}`}
                className="border-b last:border-b-0 hover:bg-green-50/40"
              >
                <td className="px-3 py-1.5 font-semibold whitespace-nowrap">
                  {item?.orderId || "-"}
                </td>

                <td className="px-3 py-1.5 whitespace-nowrap">
                  {item?.dailyOrderId ?? "-"}
                </td>

                <td className="px-3 py-1.5 max-w-[220px] break-words">
                  {item?.orderedItem || "-"}
                </td>

                <td className="px-3 py-1.5 max-w-[220px] break-words">
                  {item?.packageName || "-"}
                </td>

                <td className="px-3 py-1.5 font-medium max-w-[220px] break-words">
                  {item?.itemName || "-"}
                </td>

                <td className="px-3 py-1.5 text-right font-semibold">
                  {item?.quantity ?? "-"}
                </td>

                <td className="px-3 py-1.5">{item?.unit || "-"}</td>

                <td className="px-3 py-1.5 text-right">
                  {item?.people ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.totalPeople ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.packageQuantity ?? "-"}
                </td>

                <td className="px-3 py-1.5">{item?.department || "-"}</td>

                <td className="px-3 py-1.5">{item?.sourceType || "-"}</td>

                <td className="px-3 py-1.5">{item?.resolutionStatus || "-"}</td>

                <td className="px-3 py-1.5 text-right">
                  {item?.orderItemId ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.packageId ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.catalogueItemId ?? "-"}
                </td>

                <td className="px-3 py-1.5 text-right">
                  {item?.reportComponentId ?? "-"}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={17} className="px-3 py-8 text-center text-gray-400">
                No order-wise preparation rows
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// const DataCoverageCard = ({ data = {} }) => {
//   const entries = Object.entries(data || {});

//   return (
//     <div className="bg-white border rounded-lg overflow-hidden">
//       <div className="px-3 py-2 border-b bg-gray-50 font-semibold text-gray-800 text-xs">
//         Data Coverage
//       </div>

//       <div className="grid lg:grid-cols-3">
//         {entries.length ? (
//           entries.map(([key, value]) => (
//             <div key={key} className="px-3 py-2 border-b lg:border-r text-xs">
//               <div className="text-[10px] uppercase tracking-wide text-gray-400">
//                 {key.replace(/([A-Z])/g, " $1").trim()}
//               </div>

//               <div className="mt-0.5 text-gray-700">{String(value || "-")}</div>
//             </div>
//           ))
//         ) : (
//           <div className="px-3 py-8 text-center text-gray-400 lg:col-span-3">
//             No data coverage details
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

const PreparationTable = ({ title, icon, rows }) => (
  <div className="bg-white border rounded-lg overflow-hidden">
    <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 font-semibold text-gray-800 text-xs">
        {icon}

        {title}
      </div>

      <span className="text-[10px] text-gray-400">{rows.length} items</span>
    </div>

    <div className="overflow-auto max-h-[320px]">
      <table className="w-full text-[11px]">
        <thead className="sticky top-0 bg-white text-gray-500 border-b z-10">
          <tr>
            <th className="text-left px-3 py-2">Item</th>

            <th className="text-right px-3 py-2 w-[85px]">Qty.</th>

            <th className="text-left px-3 py-2 w-[80px]">Unit</th>

            <th className="text-right px-3 py-2 w-[65px]">Orders</th>
          </tr>
        </thead>

        <tbody>
          {rows.length ? (
            rows.map((item, index) => (
              <tr
                key={`${item?.department}-${item?.itemName}-${item?.unit}-${index}`}
                className="border-b last:border-b-0 hover:bg-gray-50"
              >
                <td className="px-3 py-1.5 font-medium text-gray-800">
                  {item?.itemName}
                </td>

                <td className="px-3 py-1.5 text-right font-semibold">
                  {item?.quantity}
                </td>

                <td className="px-3 py-1.5 text-gray-600">{item?.unit}</td>

                <td className="px-3 py-1.5 text-right text-gray-600">
                  {item?.orderCount}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} className="px-3 py-8 text-center text-gray-400">
                No items
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default KitchenReport;
